/**
 * Geo Copy Packs — Step 21.
 *
 * Bounded static copy for 5 supported countries + default fallback.
 * No LLM. No dynamic content. Fully testable.
 *
 * Usage:
 *   const pack = getGeoPack(geo.country);
 *   <h1>{pack.heroHeadline || t("home.heroTitle")}</h1>
 */

export type GeoPackKey = "FR" | "DE" | "GB" | "US" | "CA" | "default";

export interface GeoPack {
  /** Country-specific headline (empty string = use locale translation). */
  heroHeadline: string;
  /** Country-specific subheadline (empty string = use locale translation). */
  heroSubheadline: string;
  /** Single banner line shown above headline — includes flag emoji. Empty = no banner. */
  bannerLine: string;
  /** Currency symbol: €, £, $, CA$ */
  currencySymbol: string;
  /** Price label used before currency amounts. */
  currencyLabel: string;
}

const GEO_PACKS: Record<GeoPackKey, GeoPack> = {
  FR: {
    heroHeadline: "L'excellence médicale accessible à l'international.",
    heroSubheadline:
      "Cliniques certifiées en Turquie, accompagnement francophone dédié et parcours premium sécurisé.",
    bannerLine: "🇫🇷 Service disponible depuis la France",
    currencySymbol: "€",
    currencyLabel: "À partir de",
  },
  DE: {
    heroHeadline: "Medizinische Exzellenz — international zugänglich.",
    heroSubheadline:
      "Zertifizierte Kliniken in der Türkei, deutschsprachige Begleitung inklusive.",
    bannerLine: "🇩🇪 Verfügbar für Patienten aus Deutschland",
    currencySymbol: "€",
    currencyLabel: "Ab",
  },
  GB: {
    heroHeadline: "International Medical Excellence, Accessible.",
    heroSubheadline:
      "Certified clinics in Turkey, dedicated English-speaking support and a premium secure journey.",
    bannerLine: "🇬🇧 Available for patients from the UK",
    currencySymbol: "£",
    currencyLabel: "From",
  },
  US: {
    heroHeadline: "World-Class Medical Care, Internationally Accessible.",
    heroSubheadline:
      "Certified clinics in Turkey — premium, seamless coordination from the US.",
    bannerLine: "🇺🇸 Serving patients from the United States",
    currencySymbol: "$",
    currencyLabel: "From",
  },
  CA: {
    heroHeadline: "International Medical Excellence, Accessible.",
    heroSubheadline:
      "Certified clinics in Turkey, dedicated bilingual support and a premium secure journey.",
    bannerLine: "🇨🇦 Available for patients from Canada",
    currencySymbol: "CA$",
    currencyLabel: "From",
  },
  default: {
    heroHeadline: "",     // caller uses locale translation
    heroSubheadline: "",  // caller uses locale translation
    bannerLine: "",       // no banner for global/unknown
    currencySymbol: "€",
    currencyLabel: "From",
  },
};

/** Returns the geo pack for a given country code (case-sensitive). Falls back to default. */
export function getGeoPack(country: string | undefined | null): GeoPack {
  if (!country) return GEO_PACKS.default;
  return GEO_PACKS[country as GeoPackKey] ?? GEO_PACKS.default;
}

/** True when the pack has geo-specific content (not the default empty pack). */
export function isActivePack(pack: GeoPack): boolean {
  return pack.bannerLine.length > 0 || pack.heroHeadline.length > 0;
}

export const SUPPORTED_GEO_COUNTRIES: readonly GeoPackKey[] = ["FR", "DE", "GB", "US", "CA"];
